// Station search and the departure board.
//
// The board asks THIS APP'S OWN SERVER, which holds the PTV credentials and
// signs the request. The search does not ask anything: the metropolitan network
// ships inside the app, so `searchStationsLocal` in network.js answers it from
// the phone, instantly and with no signal.
//
// THERE IS NO SAMPLE FALLBACK ON THE REAL BOARD. When the server cannot answer
// — no credentials, no network, PTV down — this throws and the screen says so.
// It used to invent six plausible trains instead, which is the one thing a
// departure board must never do: somebody walks to a platform for a service
// that was never scheduled.
//
// NOR IS THERE ONE ON THE DEMO BOARD ANY MORE. `/demo` reads these endpoints
// exactly as `/app` does, because a sample board is a picture of trains that
// do not exist shown to somebody who can see the real ones from where they are
// standing. The sample below survives for reproducible screenshots alone —
// `/demo?fixture=sample` and the QA runs, which is what `sampleData` means —
// and it is the only thing that reaches it.

import { ref } from 'vue';
import { API_BASE } from './core/native.js';
import { sampleData } from './demo.js';
import { METRO_LINES } from './ptv.js';
import { cached, remember } from './cache.js';
import { prefs } from './core/storage.js';
import { city, cityQuery, currentCity } from './city.js';
import { askPatiently, busyError } from './busy.js';
import {
  allStations, lineName, lineStops, metresBetween, searchStationsLocal, stationById,
} from './network.js';
// Demo only, and the import is one way round a cycle that already exists:
// journey.js reads `directionOf` from here, network.js reads
// `normaliseStationName`. Nothing below runs at module load, so the partial
// namespace an in-flight module hands back is never read.
import { corridorsThrough, directionBetween } from './journey.js';

/**
 * What a screen says when there is no answer to be had. One sentence, one voice.
 *
 * A FUNCTION, because the source has a name and the name is not the same in
 * both cities. "Couldn't reach Public Transport Victoria" is not a sentence
 * anybody in Melbourne says, so `sourceShort` is what appears — PTV there,
 * TfNSW in Sydney.
 */
const unreachable = () => `Couldn’t reach ${city().sourceShort}. Pull down to try again.`;

/**
 * True when the last answer came from the sample, not PTV.
 *
 * Only ever true where the sample is in force — `/demo?fixture=sample` and the
 * QA runs. It is read by `checkStation`, which must not "repair" a station
 * against real data when the board it came from was invented. A live `/demo`
 * board IS real data, so it repairs like any other.
 */
export const departuresAreSample = ref(sampleData.value);

/**
 * How far ahead the last board looked, in minutes.
 *
 * `WINDOW_MINUTES` in `server/ptv.mjs` and `server/tfnsw.mjs`, sent back on
 * every board as `windowMinutes`. It is read here because `src/stopping.js`
 * cannot answer "does this train stop there" without it: a run missing from
 * the destination's board means EXPRESS only if the destination board reaches
 * far enough ahead to have shown it, and past that horizon the only honest
 * answer is nothing at all.
 *
 * A REF WITH A DEFAULT rather than a constant, because the number belongs to
 * the server and a second copy of it here would be the copy that was wrong the
 * day the window changed. The default is what every deployed server has always
 * sent, so a board that answered before this field existed is not a board with
 * no horizon — it is a board with the horizon it has always had.
 */
export const boardWindowMinutes = ref(120);

/**
 * One signed call, with the city on the end of it.
 *
 * `cityQuery()` is `&city=sydney` or the empty string, and the empty string is
 * the point: every one of these endpoints has answered for Melbourne since the
 * first build, and a request with no `city` still means Melbourne. So a
 * deployed server needs no change for Sydney to arrive, and a Melbourne URL is
 * byte-for-byte the URL it has always been — which matters, because these URLs
 * are cache keys on the server and in the browser.
 *
 * Every caller below passes a path that ALREADY has a query string, so the
 * ampersand form is the only one used; `cityFirstQuery` in city.js is there for
 * a path that does not, and `/api/disruptions` in ptv.js is the one.
 */
/**
 * @param timeoutMs how long this particular question is worth waiting for.
 *   Six seconds by default, not twelve. A departure board is a NOW instrument:
 *   someone standing at a barrier will not wait twelve seconds, and a slow
 *   answer to "when is my train" is worth less than a fast admission that we
 *   do not know. A stopping pattern is different and says so at its call site.
 */
async function ask(path, { timeoutMs = 6_000 } = {}) {
  // ONE ATTEMPT, AND ONE RETRY IF THE GATE SAID BUSY. `askPatiently` runs this
  // again after the `Retry-After` the server named, and only for a 503 that
  // names itself `busy` — a timeout, a 502 and an unconfigured server go
  // straight back to the caller as they always did. See `src/busy.js`.
  return askPatiently(async () => {
    // A SECOND TRY ON A DEAD CONNECTION, NOT ON A REFUSAL. Opening the app
    // after a long while closed, the first request often dies on a socket the
    // phone dropped or a radio still waking, and the board said "Couldn't reach
    // PTV" about a server that was fine a second later. A thrown fetch (never an
    // HTTP answer) is retried once, with more room, before it is called a failure.
    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        const res = await fetch(`${API_BASE}${path}${cityQuery()}`, {
          signal: AbortSignal.timeout(attempt ? timeoutMs + 4_000 : timeoutMs),
        });
        const body = await res.json().catch(() => null);
        return { ok: res.ok, body, status: res.status };
      } catch {
        if (attempt === 0) await new Promise((r) => setTimeout(r, 600));
      }
    }
    // No server, no signal, or the deadline passed twice. The shape a caller
    // already handles, so nothing below needs a second failure path.
    return { ok: false, body: null, status: 0 };
  });
}

/**
 * The failure to throw for an answer that did not arrive.
 *
 * A BUSY ANSWER IS NOT AN UNREACHABLE ONE, and this is the single place that
 * keeps the two apart on the way to a screen. Every caller below threw
 * `unreachable()` at both, which is how the server's careful distinction
 * turned into "Couldn't reach PTV" on a planned journey.
 */
const failure = (answer) => (answer?.busy ? busyError(answer) : new Error(unreachable()));

/**
 * Stations matching a name, disambiguated by suburb.
 *
 * Local, and therefore instant and offline. Kept async because every caller
 * awaits it and the server route still exists for anything else that wants it.
 */
export async function searchStations(term) {
  const q = String(term ?? '').trim();
  if (q.length < 2) return [];
  return searchStationsLocal(q);
}

/**
 * The next trains from a station. Throws rather than inventing any.
 *
 * `routes` narrows the board to a set of route ids, and the trip planner is not
 * being fastidious when it passes them: the server caps a board at thirty rows,
 * which at Richmond is about twenty-five minutes across eight lines, so the
 * connection twenty minutes out falls off the end of an unfiltered board and
 * the plan comes back saying there is no train.
 */
/**
 * @param speculative true when nobody is waiting on this board: the far board
 *   a destination chip reads to annotate rows, and the boards a plan the user
 *   has not asked for is worked out from. It becomes `bg=1`, which the server
 *   reads as background priority. See `server/gate.mjs`.
 */
/**
 * @param patient true when this board is a step inside a trip plan rather than
 *   a screen somebody is watching fill. It waits ten seconds instead of six
 *   and tells the server it will, because a plan's second board is read after
 *   the first leg's patterns have already spent the burst.
 */
export async function fetchDepartures(stopId, {
  routes = null, max = null, speculative = false, patient = false, to = null,
} = {}) {
  if (sampleData.value) return filterRoutes(sampleDepartures(stopId), routes);

  const answer = await ask(
    `/api/departures?stop=${Number(stopId)}${routesQuery(routes)}${maxQuery(max)}`
    + `${speculative ? '&bg=1' : ''}${patient ? '&patient=1' : ''}${to ? `&to=${Number(to)}` : ''}`,
    { timeoutMs: patient ? 10_000 : 6_000 },
  );
  const { ok, body } = answer;
  if (ok && body?.configured && Array.isArray(body.departures)) {
    // Only off a board that actually arrived. A failed request says nothing
    // about how far ahead the server looks, and letting it move this would
    // narrow the horizon on the strength of a timeout.
    const window = Number(body.windowMinutes);
    if (Number.isFinite(window) && window > 0) boardWindowMinutes.value = window;
    // Stamped with the destination asked about, so a cached or in-flight board
    // is never read as an answer for a different one. See `timetableVerdict`.
    const rows = to
      ? body.departures.map((r) => (r && r.reaches !== undefined ? { ...r, reachesTo: Number(to) } : r))
      : body.departures;
    return oneRowPerTrain(rows);
  }
  throw failure(answer);
}

/**
 * The DESTINATION's board, read only to put a verdict on the rows of this one.
 *
 * DEEP AND UNCUT, because the verdict is a match on run refs and a train can
 * only be matched if the far board reaches the minute it gets there. PTV sends
 * `max` rows per route and direction, and eight was not enough: at State
 * Library the Sunbury-bound rows ran out forty minutes in, so every tunnel
 * train from Carnegie after that had nothing to be matched against, came back
 * `unknown`, and sat under "Other trains" while calling at State Library every
 * ten minutes. Sixteen reaches the board's two-hour window at any frequency
 * this network runs, and `patient` lifts the thirty-row display cap, which at
 * a city station with six route-directions would cut the board again.
 *
 * Still `speculative`: the server reads that first, so it keeps background
 * priority and never outranks the board somebody is looking at.
 */
export const FAR_BOARD_MAX = 16;
export function fetchFarBoard(stopId) {
  return fetchDepartures(stopId, { speculative: true, patient: true, max: FAR_BOARD_MAX });
}

/**
 * The trains that have already left a station, most recent first.
 *
 * ASKED ONLY WHEN SOMEBODY PULLS FOR THEM — see the DEPARTED band on the
 * board. `bg=1` is sent for the record, though the server reads every one of
 * these as background whatever the client says: nobody's journey waits on the
 * train they already missed.
 *
 * AN ANSWER THAT DOES NOT SAY `departed` IS NOT ONE. A server from before this
 * existed ignores the parameter and answers with the upcoming board, and
 * drawing that under DEPARTED would put the next train in the list of the ones
 * that went. So such an answer is an empty band, not an error: the server has
 * nothing to say about the past, and it has said so the only way it can.
 *
 * The sample board has no past, so the sample band is empty.
 */
export async function fetchDeparted(stopId) {
  if (sampleData.value) return [];
  const answer = await ask(`/api/departures?stop=${Number(stopId)}&departed=1&bg=1`);
  const { ok, body } = answer;
  if (ok && body?.configured && Array.isArray(body.departures)) {
    return body.departed === true ? oneRowPerTrain(body.departures) : [];
  }
  throw failure(answer);
}

/**
 * One row per TRAIN.
 *
 * PTV answers a departures call per ROUTE, and where two routes share the
 * track the same train comes back once per route — every V/Line train through
 * Lara is on the Geelong route and the Warrnambool route both. It is not even
 * the same run: PTV gives the two copies different run refs (23959 on Geelong,
 * 83562 on Warrnambool, same minute, same destination, same terminus), so a
 * run-ref key finds nothing. A train is the same train when it leaves this
 * stop at the same scheduled minute for the same terminus; that is the key.
 *
 * The copy kept is the one PTV's own direction names ("Geelong" for a Waurn
 * Ponds train), else the one whose route ends where the train is going, else
 * the first listed. Order is untouched.
 */
export function oneRowPerTrain(rows) {
  const byTrain = new Map();
  const out = [];
  const keyOf = (r) => {
    const when = r?.scheduledAt ?? r?.estimatedAt;
    if (!when) return null;
    return `${when}|${String(r?.destination ?? '').trim().toLowerCase()}|${r?.finalStopId ?? ''}`;
  };
  const named = (r) => normaliseStationName(r?.routeName) === normaliseStationName(r?.directionName);
  const ends = (r) => lineStops(r?.routeId).some((s) => Number(s.stopId) === Number(r?.finalStopId));
  const score = (r) => (named(r) ? 2 : 0) + (ends(r) ? 1 : 0);
  for (const row of Array.isArray(rows) ? rows : []) {
    const key = keyOf(row);
    if (!key) { out.push(row); continue; }
    const seen = byTrain.get(key);
    if (seen == null) {
      byTrain.set(key, out.length);
      out.push(row);
      continue;
    }
    if (score(row) > score(out[seen])) out[seen] = row;
  }
  return out;
}

/**
 * One row per train AT THE PLATFORM, for the board somebody reads.
 *
 * `oneRowPerTrain` joins copies of one train that share a scheduled minute. A
 * DISRUPTED tunnel line produces a second kind of copy it cannot see: State
 * Library, 15 Oct 17:29, the Sunshine delays had run 967091 (due 17:02) half
 * an hour late and PTV estimated it at 17:29 from platform 2 — the minute the
 * on-time 967097 to the same terminus was due from the same platform. The real
 * train (a vehicle, a position) was the late one; the other run's estimates did
 * not even run in order down the line. The board drew "East Pakenham 4 min" twice.
 *
 * Two departures are one train here when they leave the SAME PLATFORM for the
 * SAME DESTINATION on the SAME LINE within a minute of each other, by their
 * best times, and at least one of them is running off its timetable. No two
 * trains leave one platform a minute apart for the same place; two trains due
 * at the same platform a minute apart on paper never pass the delay test, so a
 * timetable that really does run that close is left as it is. Rows without a
 * platform, a time or a ref are never joined: there is nothing to join them on.
 *
 * The copy kept is the one `keep` names (a train being followed), else the
 * later-running one — the run whose estimate came from a train that is
 * actually there — else the first listed. Order is untouched.
 *
 * `anchors` are trains already on screen somewhere else — the FOLLOWING rows
 * above the board — in the same shape. They are never drawn by this list, but
 * a row in an anchor's slot that is not the anchor's own run is the same train
 * drawn twice, and goes. Without them the followed run could be off the board
 * (gone from the feed, or followed from its twin) while its twin sat under
 * FOLLOWING as a second train.
 */
export const SAME_SLOT_MS = 60_000;
export function oneRowPerSlot(rows, { keep = null, anchors = [] } = {}) {
  const list = Array.isArray(rows) ? rows : [];
  const best = (r) => Date.parse(r?.estimatedAt ?? r?.scheduledAt ?? '');
  const late = (r) => {
    const s = Date.parse(r?.scheduledAt ?? '');
    const e = Date.parse(r?.estimatedAt ?? '');
    return Number.isFinite(s) && Number.isFinite(e) ? e - s : 0;
  };
  const slotOf = (r) => {
    if (!r?.runRef || r.platform == null || r.platform === '' || r.cancelled) return null;
    if (!Number.isFinite(best(r))) return null;
    const dest = String(r.destination ?? '').trim().toLowerCase();
    if (!dest) return null;
    return `${r.throughRouteId ?? r.routeId}|${String(r.platform)}|${dest}`;
  };
  const kept = (a, b) => {
    const ka = keep?.(a) ? 1 : 0;
    const kb = keep?.(b) ? 1 : 0;
    if (ka !== kb) return ka > kb ? a : b;
    return late(b) > late(a) ? b : a;
  };
  const twins = (other, row) => Math.abs(best(other) - best(row)) <= SAME_SLOT_MS
    && String(other.runRef) !== String(row.runRef)
    && Math.max(Math.abs(late(other)), Math.abs(late(row))) >= SAME_SLOT_MS;
  const held = (Array.isArray(anchors) ? anchors : []).filter((a) => slotOf(a));
  const out = [];
  const bySlot = new Map();
  for (const row of list) {
    const slot = slotOf(row);
    if (!slot) { out.push(row); continue; }
    // The twin of a train already on screen above the board.
    if (!keep?.(row) && held.some((a) => slotOf(a) === slot && twins(a, row))) continue;
    const same = (bySlot.get(slot) ?? []).find((i) => twins(out[i], row));
    if (same == null) {
      bySlot.set(slot, [...(bySlot.get(slot) ?? []), out.length]);
      out.push(row);
      continue;
    }
    out[same] = kept(out[same], row);
  }
  return out;
}

/** The old name, kept for the one caller that still uses it. */
export const oneRowPerRun = oneRowPerTrain;

/**
 * `&max=16` — a DEEPER board, and only the trip planner ever asks for one.
 *
 * Absent by default, so the board's own request is the URL it has always been
 * and the edge cache entry the whole app shares is untouched. The planner asks
 * when the connection it must confirm is an hour out: eight rows per route and
 * direction is eighty minutes of a tunnel line, and a Geelong train reaching
 * Footscray at 08:27 needs the board to see past that.
 */
function maxQuery(max) {
  const n = Number(max);
  return Number.isInteger(n) && n > 0 ? `&max=${n}` : '';
}

/** `&routes=4,11,14` — sorted and deduped, so the same board is the same URL and the same cache entry. */
function routesQuery(routes) {
  const ids = routeIdSet(routes);
  return ids.length ? `&routes=${ids.join(',')}` : '';
}

function routeIdSet(routes) {
  const ids = (Array.isArray(routes) ? routes : [])
    .map(Number)
    .filter((n) => Number.isFinite(n) && n > 0);
  return [...new Set(ids)].sort((a, b) => a - b);
}

function filterRoutes(rows, routes) {
  const ids = new Set(routeIdSet(routes));
  return ids.size ? rows.filter((d) => ids.has(Number(d.routeId))) : rows;
}

// ── One train's own stopping pattern ─────────────────────────────────────────

/** A pattern is worth having for about as long as it takes to read the card. */
const RUN_TTL_MS = 30_000;
const runCache = new Map();

/**
 * Every stop one run makes, with times. The ground truth for a planned leg.
 *
 * Cached IN MEMORY ONLY, and never through `remember()`. Everything else this
 * app stores is worth something when it is old — the ledger, the saved
 * stations, the last disruptions with their age on screen — but a run pattern
 * is a promise about the next forty minutes, and a stale one restored from disk
 * would draw a card full of times that have already happened. Gone on reload is
 * the correct behaviour.
 */
/**
 * @param routeId  the line this run is on, passed through to the server so it
 *   knows which of PTV's two route_types to ask under. A RUN REF DOES NOT SAY
 *   WHICH: ref "8828" is a real metropolitan run AND a real Ballarat run, and
 *   both answer 200. Absent, the server reads it as metropolitan, which is
 *   what every caller meant before V/Line arrived — so a call site that does
 *   not know the line is not broken, it is simply metropolitan-only.
 * @param plan  the trip planner, which needs stops and continues, not a GTFS
 *   overlay. Becomes `plan=1`. The result is not written into the run cache.
 */
export async function fetchRun(ref, routeId = null, { speculative = false, plan = false } = {}) {
  // Keyed by both, because the ref alone is not unique across route types and
  // one entry for two trains would hand the wrong pattern to whichever card
  // asked second.
  const key = `${Number(routeId) || 0}:${String(ref)}`;
  const hit = runCache.get(key);
  if (hit && Date.now() - hit.at < RUN_TTL_MS) return hit.run;

  if (sampleData.value) {
    const run = sampleRun(String(ref));
    runCache.set(key, { at: Date.now(), run });
    return run;
  }

  const line = Number(routeId) > 0 ? `&route=${Number(routeId)}` : '';
  // TEN SECONDS, NOT SIX. A pattern is what a trip plan is made of, and the
  // gate may hold one for four seconds before it even reaches PTV. Six was a
  // deadline that expired while the server was queueing politely on our
  // behalf, which turned a wait into a failure. Nobody is staring at a blank
  // pattern the way they stare at a blank board: the plan is already saying it
  // is working.
  const answer = await ask(
    `/api/run?ref=${encodeURIComponent(String(ref))}${line}`
      + `${speculative ? '&bg=1' : ''}${plan ? '&plan=1' : ''}`,
    { timeoutMs: 10_000 },
  );
  const { ok, body } = answer;
  if (!ok || !body?.configured || !body.run) throw failure(answer);

  // A PLAN RESULT IS PTV'S PATTERN WITHOUT THE GTFS OVERLAY. Writing it into
  // the shared cache would hand the train card those times for thirty seconds
  // after somebody planned past it. The planner's own gate already memoises
  // for the life of one plan.
  if (!plan) runCache.set(key, { at: Date.now(), run: body.run });
  return body.run;
}

// ── Every train on one line, right now ───────────────────────────────────────

/**
 * How long a coordinate is worth having: fifteen seconds.
 *
 * The same reasoning as `fetchRun`'s cache one screen up, only harder. A run
 * pattern is a promise about the next forty minutes and is still worth
 * something when it is a little old; a POSITION is worth nothing after the
 * fact — it is a claim about where a train is, and a train that was at Malvern
 * a minute ago is not at Malvern. So this is memory-only, never `remember()`,
 * and short enough that the line view's thirty-second poll always asks.
 *
 * Gone on reload is the correct behaviour: a restored marker would draw a
 * train that has since gone four stops.
 */
const TRAINS_TTL_MS = 15_000;
const trainsCache = new Map();

/**
 * The trains PTV can currently see on one line. Throws rather than inventing any.
 *
 * Empty is a real answer — at 2 a.m. there are none — and it is NOT the same
 * answer as a failed fetch, which throws. The line view keeps its last markers
 * on a throw and says how old they are; it says "no trains reported" only for
 * an empty array that actually arrived.
 */
export async function fetchTrains(routeId) {
  const id = Number(routeId);
  const key = String(id);
  const hit = trainsCache.get(key);
  if (hit && Date.now() - hit.at < TRAINS_TTL_MS) return hit.trains;

  if (sampleData.value) {
    const trains = sampleTrains(id);
    trainsCache.set(key, { at: Date.now(), trains });
    return trains;
  }

  const answer = await ask(`/api/trains?route=${id}`);
  const { ok, body } = answer;
  if (!ok || !body?.configured || !Array.isArray(body.trains)) throw failure(answer);

  trainsCache.set(key, { at: Date.now(), trains: body.trains });
  return body.trains;
}

/**
 * The lines the map can see, declared to the server, answered from its cache.
 *
 * ONE REQUEST FOR THE WHOLE SCREEN, and it is a DECLARATION as much as a
 * question: the server records which lines are being watched and keeps those,
 * and only those, warm on a budget it controls. What the app asks PTV for
 * therefore stops depending on how many people have the map open — five phones
 * watching the same six lines cost what one phone costs — and stops depending
 * on what any one client decides to request.
 *
 * NO CLIENT-SIDE CACHE HERE, unlike `fetchTrains` above. The server's answer IS
 * the cache; caching a cache would only mean the interest went undeclared for
 * fifteen seconds at a time, and interest that is not declared expires.
 *
 * Throws on an unreachable server, like everything else here. The rows it
 * returns carry their own honesty: `published: false` for an operator that
 * does not report positions, `pending` for a line not yet read, `stale` for a
 * reading too old to draw. The map prints all three rather than an empty map.
 */
export async function fetchWatched(routeIds = [], {
  client = '', freshMs = 0, moved = false,
} = {}) {
  const ids = [...new Set(routeIds.map(Number).filter((n) => Number.isInteger(n) && n > 0))];

  if (sampleData.value) {
    return {
      lines: ids.map((routeId) => ({
        routeId, trains: sampleTrains(routeId), published: true, pending: false, stale: false,
      })),
      pending: 0,
      unpublished: 0,
      stale: 0,
      retryAfterMs: null,
    };
  }

  // `fresh` in SECONDS, because it is a number somebody reads in a log; the
  // server clamps it either way. `moved` says this viewer panned or zoomed
  // since they last spoke, which clears the server's quiet backoff on the lines
  // they can see.
  const extra = [
    client ? `&client=${encodeURIComponent(client)}` : '',
    freshMs ? `&fresh=${Math.round(freshMs / 1000)}` : '',
    moved ? '&moved=1' : '',
  ].join('');
  const answer = await ask(`/api/watch?routes=${ids.join(',')}${extra}`);
  const { ok, body } = answer;
  if (!ok || !body?.configured || !Array.isArray(body.lines)) throw failure(answer);

  return {
    lines: body.lines,
    pending: Number(body.pending) || 0,
    unpublished: Number(body.unpublished) || 0,
    stale: Number(body.stale) || 0,
    retryAfterMs: Number(body.retryAfterMs) || null,
  };
}

/**
 * This viewer has stopped looking at the map.
 *
 * THE HONEST HALF OF DECLARING INTEREST. Without it the server can only forget
 * a viewer by waiting out a timer, so a phone in a pocket goes on costing PTV
 * requests for two and a half minutes after the screen went dark. With it, the
 * lines stop being refreshed within the second.
 *
 * `keepalive` because the usual moment to call this is the page being hidden or
 * torn down, when an ordinary fetch is cancelled with it. Failure is silent and
 * has to be: the backstop timer is still there, and nothing on any screen
 * depends on this landing.
 */
export async function releaseWatch(client) {
  if (!client || sampleData.value) return;
  try {
    await fetch(`${API_BASE}/api/watch?routes=&release=1&client=${encodeURIComponent(client)}${cityQuery()}`, {
      keepalive: true,
      signal: AbortSignal.timeout(2_000),
    });
  } catch { /* the server's own timer is the backstop */ }
}

// ── One station, as a building ───────────────────────────────────────────────

/**
 * How long a lift is a lift.
 *
 * A day, and PERSISTED — which is the opposite of what `fetchRun` above does,
 * deliberately. A stale run pattern draws a card full of times that have
 * already happened; a stale car park is still a car park. The facts on this
 * screen change about once a year, and the station page opening instantly on a
 * platform with no signal is the whole reason it is worth caching at all.
 */
const STATION_TTL_MS = 24 * 60 * 60_000;

/** Five saved stations, the one you are standing at, and a little room. */
const STATION_KEEP = 8;

/**
 * What the operator publishes about a station's facilities, or null.
 *
 * A cached answer is handed back even when the request fails, because an old
 * lift is better than no page — unlike a board, nothing here is a claim about
 * the next forty minutes.
 *
 * NULL IS A REAL ANSWER AND IS NOT A FAILURE. A server that replies
 * `{ configured: true, station: null }` has told us it looked and there is
 * nothing published — which is what Sydney answers for the whole of this
 * spike, and what PTV answers for plenty of suburban stops. This used to
 * throw on it, so the screen said "Couldn't reach PTV" about a request that
 * had arrived and been answered: the app blaming the network for its own
 * silence. The page distinguishes the two now.
 */
export async function fetchStation(stopId) {
  const id = Number(stopId);
  if (sampleData.value) return sampleStation(id);

  const key = `station:${id}`;
  const hit = cached(key, STATION_TTL_MS);
  if (hit.fresh && hit.value) return hit.value;

  const answer = await ask(`/api/station?stop=${id}`);
  const { ok, body } = answer;

  if (!ok || !body?.configured) {
    if (hit.value) return hit.value;
    throw failure(answer);
  }

  if (!body.station) return hit.value ?? null;

  remember(key, body.station, { cap: STATION_KEEP });
  return body.station;
}

// ── What the trip costs ──────────────────────────────────────────────────────

/**
 * The fare between two zones.
 *
 * MEMORY ONLY, and not through `remember()`. There are about three live zone
 * pairs on the whole network and the server already caches them for everybody,
 * so a second request in a session is nearly free — while a price written to
 * disk is a number that could be quoted at a ticket machine a fare rise later.
 * Gone on reload is the correct behaviour for money.
 */
const fareCache = new Map();

export async function fetchFare(min, max) {
  const key = `${Number(min)}:${Number(max)}`;
  if (fareCache.has(key)) return fareCache.get(key);

  if (sampleData.value) {
    fareCache.set(key, SAMPLE_FARE);
    return SAMPLE_FARE;
  }

  const answer = await ask(`/api/fare?min=${Number(min)}&max=${Number(max)}`);
  const { ok, body } = answer;
  if (!ok || !body?.configured) throw failure(answer);

  // Answered, with no price. Sydney says this for the whole spike — Opal is
  // priced on distance, not on zones, so there is no zone pair to quote — and
  // the journey page already omits the fare line for a null. Cached like any
  // other answer, so a screenful of options does not ask five more times.
  fareCache.set(key, body.fare ?? null);
  return body.fare ?? null;
}

/**
 * A few stations to offer before anyone has searched.
 *
 * An empty state that says "pick the one you leave from" and then shows an
 * empty box has asked a question and supplied no way to answer it. These are
 * one tap each, and stop the first run being a spelling test.
 *
 * Read out of the bundled network, in this order: the handful of interchanges
 * most of the city passes through, rather than whichever rows the old sample
 * happened to carry. Which ones those are is a fact about a city, so the names
 * are `CITIES[…].suggested` and the rows are looked up here.
 */
export function suggestedStations() {
  const rows = allStations();
  return (city().suggested ?? [])
    .map((name) => rows.find((s) => normaliseStationName(s.name) === normaliseStationName(name)))
    .filter(Boolean);
}

// ── Several stations, in the order they were chosen ──────────────────────────
//
// A commuter leaves from more than one station: home in the morning, work in
// the evening. The saved list has always held several rows; these two are the
// small rules that stop the extra ones reading as a pile.

/**
 * The saved rows with the default first, everything else in the order it is in.
 *
 * `stations` is stored newest-first, so the one the board opens on can sit
 * third in a list whose whole job is to say which one that is. Pure and
 * non-mutating — the caller's array is left alone.
 */
export function defaultFirst(rows = []) {
  const list = [...rows];
  const at = list.findIndex((r) => Boolean(r?.isDefault));
  if (at <= 0) return list;
  const [row] = list.splice(at, 1);
  return [row, ...list];
}

/**
 * "Carnegie", "Carnegie and State Library", "Carnegie, State Library and Richmond".
 *
 * Written out rather than counted, because "2 stations" is a number somebody
 * has to expand and the names are what they just chose. Oxford comma left off:
 * this is a sentence in the app's voice, not a list.
 */
export function stationPhrase(names = []) {
  const clean = names.map((n) => String(n ?? '').trim()).filter(Boolean);
  if (clean.length <= 1) return clean[0] ?? '';
  return `${clean.slice(0, -1).join(', ')} and ${clean[clean.length - 1]}`;
}

/**
 * Which way a departure is going, from where you are standing.
 *
 * Not a property of the train — a property of the train AND the platform. Since
 * the Metro Tunnel opened, Pakenham and Cranbourne services continue as Sunbury
 * ones and back, so the citybound train at Carnegie is signed SUNBURY: a
 * terminus on the far side of the city. Read as a destination alone it looks
 * outbound, and a commuter sent to platform 2 on that reading has missed their
 * train and is watching it leave the other way.
 *
 * Two rules, in order:
 *
 * 1. PTV names a citybound direction for the city end it reaches — "City
 *    (Flinders Street)" on the old network, a tunnel station on the new one.
 * 2. Otherwise, a train on a route this station does NOT serve USUALLY has to
 *    cross the city to get to its own line. It is going in — unless the
 *    geography says otherwise; see `mustCrossCity` below.
 *
 * Anything left is a train running further out on a line that is already here.
 *
 * `stopId` is where you are standing, and rule 2 is only as good as having it:
 * without one the rule keeps its old, blunter form. Every caller that knows the
 * station passes it.
 */
const CITY_DIRECTION = /\bcity\b|flinders street|parliament|melbourne central|town hall|state library|anzac/i;

export function directionOf(departure, stationLines = [], { stopId = null } = {}) {
  // The direction NAME, not the destination: the board now says SUNBURY for a
  // citybound Carnegie train, but PTV's direction for it is still City.
  const named = String(departure?.directionName ?? departure?.destination ?? '');
  if (CITY_DIRECTION.test(named)) return 'citybound';
  // Or the city end itself, by the current city's own list, on EITHER the
  // direction name or the destination: V/Line's direction is the word
  // "Melbourne" and its destination "Southern Cross", and only the second is
  // a station. The regex above is Melbourne's metro vocabulary; this is every
  // city's, and it is what puts a Sydney train to Central under Towards.
  const ends = (city().cityEnd ?? []).map((name) => normaliseStationName(name));
  for (const word of [departure?.directionName, departure?.destination]) {
    const flat = normaliseStationName(word);
    if (flat && ends.includes(flat)) return 'citybound';
  }
  if (/\bmelbourne\b/i.test(named) && city().apiCity == null) return 'citybound';

  // Rule 2 needs a route to reason about. A row with no usable routeId is not
  // evidence of a through-runner, so it stays on the outbound board rather than
  // being asserted into the one somebody is timing their walk by.
  const routeId = Number(departure?.routeId);
  const serves = new Set(stationLineIds(stationLines));
  if (serves.size && Number.isFinite(routeId) && routeId > 0 && !serves.has(routeId)) {
    // …but only if the train really does have to go through the city to get
    // where it is going. A football special is the case that proves it must be
    // asked: route 11 at South Yarra on a match night is a train to Dandenong,
    // leaving AWAY from the city, on a line the bundle correctly says South
    // Yarra is not on.
    return mustCrossCity(departure, stopId ?? departure?.stopId) === false
      ? 'outbound'
      : 'citybound';
  }

  return 'outbound';
}

/**
 * Does this train have to pass through the city to reach its terminus from
 * here? `true`, `false`, or `null` where there is nothing to measure.
 *
 * Rule 2 above is a good rule with one silent assumption: that a route whose
 * stop list leaves this station out is a route on the OTHER SIDE of the city.
 * That is true of the case it was written for — a Sunbury train at Carnegie —
 * and false of an event special, which runs on this side of town on a line
 * whose everyday service no longer comes this way. The bundle cannot tell the
 * two apart, because both are "a route this station is not on". The map can.
 *
 * So: compare how far the train's terminus is FROM HERE with how far it is
 * from the nearest city-end station. A Sunbury train at Carnegie is a terminus
 * further from Carnegie than from Southern Cross — Carnegie is on the wrong
 * side, the train must come in through the city, and it is citybound. The
 * 17:45 to Dandenong at South Yarra is a terminus NEARER from here than from
 * any city end: this station is already on the way, and the train is leaving.
 *
 * Straight-line distances, deliberately. This is a question about which side of
 * town two points are on, not about track kilometres, and the bundle's
 * coordinates answer it without a route graph or a single request.
 *
 * NULL, NOT A GUESS, where the terminus has no stop id in this bundle or the
 * city lists no ends. The caller keeps the old rule on a null, because an
 * unmeasurable row is not evidence of anything.
 */
function mustCrossCity(departure, stopId) {
  const here = stationById(stopId);
  const terminus = stationById(departure?.finalStopId)
    ?? allStations().find((s) => normaliseStationName(s.name) === normaliseStationName(departure?.destination));
  if (!here || !terminus) return null;

  const ends = (city().cityEnd ?? [])
    .map((name) => allStations().find((s) => normaliseStationName(s.name) === normaliseStationName(name)))
    .filter(Boolean);
  if (!ends.length) return null;

  const fromHere = metresBetween(here.lat, here.lng, terminus.lat, terminus.lng);
  const fromCity = Math.min(...ends.map((e) => metresBetween(e.lat, e.lng, terminus.lat, terminus.lng)));
  return fromHere > fromCity;
}

/** The route ids a station serves, from the array PTV sends or the row's stored "4,11". */
export function stationLineIds(lines) {
  const raw = Array.isArray(lines) ? lines : String(lines ?? '').split(',');
  const ids = raw.map(Number).filter((n) => Number.isFinite(n) && n > 0);
  return [...new Set(ids)];
}

/**
 * Whether splitting this station's board by direction tells anyone anything.
 *
 * At Flinders Street or Melbourne Central every train is leaving the city and
 * rule 2 has nothing to bite on: a heading would be a claim the data cannot
 * support. Same where a saved row predates line ids and we simply do not know
 * what the station serves — one honest list beats two confident ones.
 */
const LOOP_STATION_LINES = 6;

export function splitsByDirection(lines) {
  const ids = stationLineIds(lines);
  return ids.length > 0 && ids.length < LOOP_STATION_LINES;
}

// ── Is this stop id really that station? ─────────────────────────────────────

/**
 * PTV calls every stop "X Station"; a saved row may or may not carry the word,
 * and neither spelling should count as a different station.
 */
export function normaliseStationName(name) {
  return String(name ?? '')
    .toLowerCase()
    .replace(/\bstation\b/g, ' ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/**
 * What to do with a saved station's stop id, given PTV's own search results
 * for its name. Pure, so the decision can be tested without a network.
 *
 * A station saved while the app was on sample data carries a GUESSED stop id,
 * and the guesses were wrong: sample Carnegie was 1029, which is Brunswick, on
 * the Upfield line — so a saved Carnegie showed Brunswick's trains, correctly
 * formatted and completely wrong. That is the worst thing this app can do, and
 * it did not look like a fault from the outside: a full board of real trains.
 *
 * Hence three answers rather than two. Correcting a knowably-wrong id is the
 * good case; where the name cannot be resolved to exactly one stop the board is
 * BLOCKED rather than shown, because an unverified id is indistinguishable from
 * a wrong one and a wrong board is worse than no board.
 */
export function stationRepair(station, matches, { live = true } = {}) {
  // On sample data there is nothing authoritative to compare against, and the
  // sample's own ids are what put the app here.
  if (!live) return { action: 'keep', reason: 'sample' };

  const wanted = normaliseStationName(station?.name);
  if (!wanted) return { action: 'keep', reason: 'unnamed' };

  const sameName = (Array.isArray(matches) ? matches : [])
    .filter((m) => normaliseStationName(m?.name) === wanted);

  if (!sameName.length) return { action: 'block', reason: 'not-found' };

  // The saved id is one of the stops PTV returns under that name: it is right,
  // and it never has to be checked again this session.
  if (sameName.some((m) => Number(m.stopId) === Number(station?.stopId))) {
    return { action: 'keep', reason: 'verified' };
  }

  if (sameName.length > 1) return { action: 'block', reason: 'ambiguous' };

  return { action: 'correct', stopId: Number(sameName[0].stopId), reason: 'wrong-id' };
}

// Checked once per stop id per session. The check costs a search request, and
// the answer does not change between two pulls of the same board.
const checked = new Set();
const checkKey = (name, stopId) => `${Number(stopId)}:${normaliseStationName(name)}`;

/**
 * Check a saved station's stop id against the bundled network, and say what to
 * do about it. The caller owns persistence — this module never writes to the
 * store.
 *
 * Resolved locally, which means it now runs on a phone with no signal, before
 * any request goes out, and cannot itself fail. The bundle is PTV's own data,
 * pulled by `scripts/fetch-network.mjs`, so it is the same authority the search
 * endpoint used to be asked for.
 *
 * Note what is NOT a condition here: an empty board. The first version of this
 * repair only ran when the live board came back with no trains, which is why it
 * never ran for Carnegie — 1029 answers with a full board, Brunswick's. A wrong
 * id is loud, not silent.
 */
export async function checkStation(station) {
  if (!station?.name) return { action: 'keep', reason: 'unnamed' };
  if (checked.has(checkKey(station.name, station.stopId))) return { action: 'keep', reason: 'checked' };

  const matches = await searchStations(station.name);
  const decision = stationRepair(station, matches, { live: !departuresAreSample.value });
  if (decision.action === 'keep' && decision.reason === 'verified') {
    checked.add(checkKey(station.name, station.stopId));
  }
  if (decision.action === 'correct') checked.add(checkKey(station.name, decision.stopId));
  return decision;
}

// ── Where you are going, per station you leave from ──────────────────────────
//
// The board's destination chip — "TO BLACKBURN ▾" — is remembered PER ORIGIN,
// not once for the app. A commuter leaves Carnegie for the city in the morning
// and State Library for Carnegie at night, and one remembered destination
// would be wrong for one of those two trips every single day.
//
// `prefs['lateagain:stopsat:v1']` is `{ '1071': 1023 }` — origin stop id to
// destination stop id, both as keys of a plain object because that is what
// survives `JSON.stringify` through the prefs proxy.
//
// ZERO IS A VALUE, and it is the one that makes the seeding bearable. The chip
// seeds itself from an active commute rule whose `from` is the station on the
// board, which is right the first time and an imposition the fourth: somebody
// who CLEARS the chip has said no, and zero is that no, recorded. Absent means
// never asked; zero means asked and declined.
//
// PER CITY, by a suffix on the key. Every id in that map — both the keys and
// the values — is a stop id in one network, so one map shared between five
// cities is four cities' worth of preferences that can never match anything.
// Melbourne keeps the bare key it has always had, so an install that predates
// this reads its own chips back exactly where it left them.

const DESTINATIONS_KEY = 'lateagain:stopsat:v1';

const destinationsKey = () => {
  const id = currentCity();
  return id === 'melbourne' ? DESTINATIONS_KEY : `${DESTINATIONS_KEY}:${id}`;
};

function destinationMap() {
  const stored = prefs[destinationsKey()];
  return stored && typeof stored === 'object' && !Array.isArray(stored) ? stored : {};
}

/**
 * The destination chosen for this origin: a stop id, 0 for "no thank you", or
 * undefined where nothing has ever been chosen.
 */
export function destinationFor(originStopId) {
  const raw = destinationMap()[String(Number(originStopId))];
  return raw === undefined ? undefined : Number(raw) || 0;
}

/**
 * Remember one, or remember the refusal.
 *
 * Writes under /demo as well, where `prefs` is memory (src/core/storage.js) —
 * so a demo chip behaves exactly like the real one for as long as the tab is
 * open and leaves nothing behind. Nothing SEEDS this key in the demo: the chip
 * is meant to arrive from the seeded commute rule, which is the behaviour
 * worth showing.
 */
export function rememberDestination(originStopId, destinationStopId) {
  const origin = Number(originStopId);
  if (!Number.isFinite(origin) || origin <= 0) return;
  prefs[destinationsKey()] = {
    ...destinationMap(),
    [String(origin)]: Number(destinationStopId) || 0,
  };
}

// ── Sample, for /demo and the QA fixtures only ───────────────────────────────
//
// Nothing below this line is reachable outside `/demo?fixture=sample` and a QA
// run. `fetchDepartures` throws in the real app rather than falling through to
// it, plain `/demo` reads the live endpoints, and the station search comes from
// the bundled network. Kept because the store screenshots have to be
// reproducible and a screenshot run cannot depend on PTV being up.

/**
 * Sample stations.
 *
 * These stop ids WERE guesses, and every guess but one was wrong — Carnegie was
 * 1029, which PTV knows as Brunswick, on the Upfield line. A station saved from
 * this list therefore carried a real id belonging to somebody else's suburb,
 * and the board it produced looked entirely healthy.
 *
 * They are now PTV's own ids, read from the live `/api/stations` endpoint.
 * `checkStation` above is the second line of defence, for rows saved before
 * this was fixed.
 */
const SAMPLE_STATIONS = [
  // Metro Tunnel. Pakenham and Cranbourne run through it, so these are the
  // city-end stops those lines actually reach now — not Flinders Street.
  { stopId: 1162, name: 'Richmond', suburb: 'Richmond', lines: [1, 2, 4, 6, 7, 9, 11, 12] },
  { stopId: 1071, name: 'Flinders Street', suburb: 'Melbourne City', lines: [1, 2, 3, 5, 6, 7, 8, 9, 12, 15, 16, 17] },
  { stopId: 1036, name: 'Caulfield', suburb: 'Caulfield East', lines: [4, 6, 11] },
  { stopId: 1072, name: 'Footscray', suburb: 'Footscray', lines: [14, 16, 17] },
  { stopId: 1034, name: 'Carnegie', suburb: 'Carnegie', lines: [4, 11] },
  { stopId: 1234, name: 'State Library', suburb: 'Melbourne City', lines: [4, 11, 14] },
];

const MIN = 60_000;

/**
 * Where trains from a station actually go.
 *
 * Written per station rather than generated, because a board whose destinations
 * are wrong is worse than one that says nothing — somebody would stand on the
 * wrong platform. Pakenham and Cranbourne run through the Metro Tunnel, so a
 * city-bound train from Carnegie reaches Town Hall and Parkville, NOT Flinders
 * Street, and the sample says so.
 */
/**
 * ROWS CARRY `final` AND `express` where the demo is making a point with them.
 *
 * `final` is the run's terminus stop id and `express` the number of stops it
 * skips — the two fields `mapDeparture` passes through from PTV's run, and the
 * two that let `src/stopping.js` answer "does this stop at mine" without a
 * second request. A demo that left them off would show the feature's slowest
 * path only, which is the path least worth looking at.
 *
 * Absent means absent, not zero: a row with no `express` is a row PTV never
 * told us about, and it takes the two-board route like any other.
 */
const DESTINATIONS = {
  1034: [   // Carnegie — Dandenong group, through the tunnel. The citybound
            // trains are signed for the far side of it: a Sunbury service from
            // Carnegie goes IN, and route 14 is not a line Carnegie serves.
    { routeId: 4, to: 'Sunbury', final: 1187, express: 0, dir: 'City (Town Hall)' },
    { routeId: 4, to: 'City (Town Hall)', final: 1235, express: 3 },
    { routeId: 11, to: 'Pakenham', final: 1153, express: 0 },
    { routeId: 4, to: 'Cranbourne', final: 1045, express: 2 },
  ],
  1234: [   // State Library — tunnel platforms
    { routeId: 11, to: 'Pakenham', final: 1153, express: 0 },
    { routeId: 11, to: 'Sunbury', final: 1187, express: 2, dir: 'City (Town Hall)' },
    { routeId: 4, to: 'Cranbourne', final: 1045, express: 0 },
    { routeId: 4, to: 'Watergardens', final: 1202, express: 0, dir: 'City (Town Hall)' },
  ],
  1162: [   // Richmond — the Burnley/Caulfield fan
    { routeId: 1, to: 'City (Flinders Street)', final: 1071, express: 0 },
    { routeId: 2, to: 'Belgrave', final: 1018, express: 5 },
    { routeId: 9, to: 'Lilydale', final: 1115, express: 0 },
    { routeId: 7, to: 'Glen Waverley', final: 1078, express: 0 },
  ],
  1071: [   // Flinders Street — the board the stopping question was written for.
            // Somebody going to Blackburn sees BELGRAVE twice and can tell them
            // apart only by what each one skips, which is the whole problem.
            // One of each verdict, in the order they are decided:
    { routeId: 2, to: 'Belgrave', final: 1018, express: 0 },     // stops — all stations
    { routeId: 9, to: 'Blackburn', final: 1023, express: 0 },    // stops — terminates there
    { routeId: 12, to: 'Sandringham', final: 1173, express: 0 }, // the cancelled row
    { routeId: 1, to: 'Alamein', final: 1002, express: 0 },      // elsewhere — another line
    { routeId: 2, to: 'Belgrave', final: 1018, express: 6 },     // express — skips it
    { routeId: 6, to: 'Frankston', final: 1073, express: 0 },    // elsewhere
  ],
  1023: [   // Blackburn — the destination board the one above is intersected
            // with. Its own trains, going on out or back in.
    { routeId: 2, to: 'City (Flinders Street)', final: 1071, express: 0 },
    { routeId: 9, to: 'Lilydale', final: 1115, express: 0 },
    { routeId: 2, to: 'Belgrave', final: 1018, express: 0 },
    { routeId: 9, to: 'City (Flinders Street)', final: 1071, express: 2 },
  ],
  1036: [   // Caulfield
    { routeId: 11, to: 'City (Parkville)', final: 1233, express: 4 },
    { routeId: 12, to: 'Sandringham', final: 1173, express: 0 },
    { routeId: 4, to: 'Cranbourne', final: 1045, express: 0 },
  ],
  1072: [   // Footscray
    { routeId: 14, to: 'Sunbury', final: 1187, express: 3 },
    { routeId: 16, to: 'Werribee', final: 1205, express: 0 },
    { routeId: 17, to: 'Williamstown', final: 1211, express: 0 },
  ],
};

/**
 * Plausible trains, generated relative to now.
 *
 * Relative rather than fixed, so the board reads as a board — a fixture frozen
 * at 07:42 makes every screenshot a lie about a different thing.
 */
// The ids this file used to guess with, and what they really are.
//
// Rows saved before the correction still carry the old numbers, and so does the
// demo seed in `src/app-state.js` — a generated file this change does not own.
// Without this, a demo board at "Carnegie" fell through to Flinders Street's
// pattern and every row came out citybound.
const GUESSED_IDS = { 1029: 1034, 1226: 1234, 1181: 1162, 1030: 1036, 1155: 1072 };

/**
 * The pattern to build a demo board from.
 *
 * The written-out ones are MELBOURNE'S, and they stay Melbourne's: they name
 * real destinations for real stops and there is no second set to hand-write
 * for every city this app might learn.
 *
 * In another city they are not a fallback, they are a lie — a board at Central
 * headed "Pakenham" is worse than no demo at all — so the shape is applied to
 * whatever bundle is loaded instead: each line through the stop, running to its
 * own far terminus. Same six rows, same one cancellation, no invented names.
 */
function samplePattern(id) {
  if (currentCity() === 'melbourne') return DESTINATIONS[id] ?? DESTINATIONS[1071];

  const station = allStations().find((s) => s.stopId === id);
  const rows = (station?.lines ?? []).map((routeId) => {
    const stops = lineStops(routeId);
    const far = stops[stops.length - 1];
    const cityEndOfLine = stops[0];
    // Two directions per line, which is what a platform actually offers.
    // Every stop called at, because a demo that modelled an express would be
    // modelling the one thing nobody looking at it can check. So `express` is
    // 0 and the terminus is the end of the line it is running to — true of the
    // train this row describes, which is all a sample has to be.
    return [
      { routeId, to: far?.name ?? 'Train', final: far?.stopId ?? null, express: 0 },
      { routeId, to: cityEndOfLine?.name ?? 'City', final: cityEndOfLine?.stopId ?? null, express: 0 },
    ];
  }).flat().filter((r) => r.to);

  return rows.length ? rows : DESTINATIONS[1071];
}

/**
 * The same train, seen again from further down the line.
 *
 * `src/stopping.js` answers "does this stop at mine" for an express by
 * intersecting two boards on `runRef` — a run on both boards stops at both.
 * Nothing in the generator above produces that overlap: every row it makes is
 * keyed `demo:<its own stop>:<i>`, so on the demo every train would be missing
 * from every other board and the whole board would read EXPRESS.
 *
 * This is the overlap, written out. Each entry says: the train that leaves
 * `from` as row `index` turns up here `minutes` later, carrying the run ref it
 * already had. Which is exactly what a run ref is for.
 */
const SAMPLE_ONWARD = {
  // Flinders Street's first Belgrave, forty-one minutes down the line. Its
  // second Belgrave is deliberately NOT here: that is the express, and its
  // absence from this board is the fact the feature reads.
  // Thirty-three minutes, which is what the bundle's own gaps sum to for this
  // stretch rather than the forty-odd a real all-stations run takes. The demo
  // shows a read time and a reckoned one on adjacent rows, and a sample where
  // the later train arrives first would read as a bug in the feature rather
  // than as a rounding difference in the sample.
  1023: [{ from: 1071, index: 0, minutes: 33 }],
};

function sampleDepartures(stopId, { onward = true } = {}) {
  const id = GUESSED_IDS[Number(stopId)] ?? Number(stopId);
  const pattern = samplePattern(id);
  const now = Date.now();
  const shape = [
    { inMin: 3, delay: 0 },
    { inMin: 7, delay: 3 },
    // EXACTLY ONE, so `/demo` shows the state without pretending it is common
    // — the live feed cancels about eleven rows in three thousand. Mid-board,
    // so the gap it leaves is visible above and below it, and with no delay
    // figure, because a train that is both cancelled and six minutes late is a
    // contradiction nobody should have to read.
    { inMin: 12, delay: null, cancelled: true },
    { inMin: 18, delay: 6 },
    { inMin: 24, delay: 0 },
    { inMin: 31, delay: null },
  ];
  const lines = SAMPLE_STATIONS.find((s) => s.stopId === id)?.lines
    ?? allStations().find((s) => s.stopId === id)?.lines
    ?? [];
  const rows = shape.map((row, i) => {
    const spec = pattern[i % pattern.length];
    const line = METRO_LINES.find((l) => l.routeId === spec.routeId)
      ?? { routeId: spec.routeId, name: lineName(spec.routeId) };
    // Platform follows the direction, not the row number. A sample that put a
    // citybound train on the outbound platform contradicted the heading above
    // it, which is exactly the kind of thing a board must never do.
    const platform = directionOf({ routeId: spec.routeId, destination: spec.to }, lines, { stopId: id }) === 'citybound' ? '1' : '2';
    const scheduled = new Date(now + row.inMin * MIN);
    return {
      routeId: spec.routeId,
      routeName: line?.name ?? 'Train',
      destination: spec.to,
      platform,
      atPlatform: row.inMin <= 1,
      // On every row, not only the cancelled one. A field that appears on some
      // rows and not others is a field every reader has to test for.
      cancelled: Boolean(row.cancelled),
      scheduledAt: scheduled.toISOString(),
      estimatedAt: row.delay == null ? null : new Date(scheduled.getTime() + row.delay * MIN).toISOString(),
      delayMinutes: row.delay,
      // A handle `sampleRun` below can take apart again. The demo has to be able
      // to PLAN, not just list — `/demo` is a store listing's first impression
      // and a Directions tab that says "couldn't reach PTV" is a bad one.
      runRef: `demo:${id}:${i}`,
      // Nulls where the pattern says nothing, never zeros. Zero is the claim
      // that a train stops everywhere, and `src/stopping.js` acts on it.
      // The demo's tunnel trains are signed for the Sunbury line but are
      // citybound at Carnegie, as PTV says with its direction name.
      directionName: spec.dir ?? '',
      expressStopCount: spec.express == null ? null : Number(spec.express),
      finalStopId: Number(spec.final) || null,
    };
  });

  return onward ? withOnward(id, rows) : rows;
}

/**
 * A station's own board, plus the trains arriving from elsewhere on it.
 *
 * `onward: false` on the recursive read, so a pair of stations that each list
 * the other cannot spin. The rows are merged in time order, because a board
 * out of order is the one thing a board may never be.
 */
function withOnward(id, rows) {
  const extra = [];
  for (const link of SAMPLE_ONWARD[id] ?? []) {
    const source = sampleDepartures(link.from, { onward: false })[Number(link.index)];
    if (!source || source.cancelled) continue;
    const at = new Date(Date.parse(source.estimatedAt ?? source.scheduledAt) + link.minutes * MIN);
    extra.push({
      ...source,
      // Its own platform here is not the one it left from, and the demo has no
      // opinion about which. Nothing reads it but the direction split, which
      // takes the destination.
      platform: null,
      atPlatform: false,
      scheduledAt: at.toISOString(),
      estimatedAt: at.toISOString(),
      delayMinutes: null,
    });
  }
  if (!extra.length) return rows;
  const when = (d) => Date.parse(d.estimatedAt ?? d.scheduledAt);
  return [...rows, ...extra].sort((a, b) => when(a) - when(b));
}

/** How far apart the demo pretends stations are. */
const SAMPLE_HOP_MS = 2 * MIN;
/** Far enough to plan a city trip against, short enough to read. */
const SAMPLE_PATTERN_STOPS = 14;

/**
 * A stopping pattern for a sample departure, built from the bundled network.
 *
 * The corridor is the real one, so a demo train from Carnegie runs through the
 * tunnel exactly as it does in life and a demo change at Richmond is a change
 * at Richmond. Only the times are invented: two minutes a station from
 * whenever the row leaves, and every stop called at, because a demo that
 * modelled an express would be modelling the one thing nobody can check.
 */
export function sampleRun(ref) {
  const [, rawStop, rawIndex] = String(ref).split(':');
  const stopId = GUESSED_IDS[Number(rawStop)] ?? Number(rawStop);
  const row = sampleDepartures(stopId)[Number(rawIndex)];
  if (!row) return null;

  const through = corridorsThrough(stopId);
  const corridor = through.find((c) => c.routeIds.includes(row.routeId)) ?? through[0];
  if (!corridor) return null;

  const at = corridor.index.get(stopId);
  const { step, last } = sampleRunPath(corridor, at, row);
  const leaves = Date.parse(row.estimatedAt ?? row.scheduledAt);

  const stops = [];
  for (let i = at; stops.length < SAMPLE_PATTERN_STOPS; i += step) {
    const station = corridor.stops[i];
    if (!station) break;
    const when = new Date(leaves + stops.length * SAMPLE_HOP_MS).toISOString();
    stops.push({
      stopId: station.stopId,
      name: station.name,
      scheduledAt: when,
      estimatedAt: null,
      platform: row.platform,
    });
    if (i === last) break;
  }

  return {
    runRef: String(ref),
    routeId: row.routeId,
    // Taken from the ROW rather than hard-coded false, so that tapping the one
    // struck-through row on the demo board opens a train card that agrees with
    // it. Every other demo row is false, which is the same thing the brief
    // asked for and one state further.
    cancelled: Boolean(row.cancelled),
    // What runs on most of the network, so the demo stamp reads like a real one.
    vehicle: {
      description: '6 Car Xtrapolis', carriages: 6, lowFloor: null, airConditioned: null,
    },
    position: samplePosition(corridor.stops[at], row),
    stops,
  };
}

/**
 * Where a demo train is: standing at the platform it is about to leave.
 *
 * `position` used to be null here, on the grounds that nothing drew it. The
 * train card draws it now — the marker phrase and the glyph on the rail both —
 * and a demo card with an empty rail is a demo of the empty state.
 *
 * THE COORDINATE IS THE STATION'S OWN, from the bundle, so `progressOn`
 * projects it exactly as it projects a live fix and the phrase it produces is
 * "AT CARNEGIE" — which is what a train three minutes from departure is
 * actually doing, and agrees with the "Leaves Carnegie 07:52" above it. A
 * coordinate invented somewhere down the line would have the card contradicting
 * its own masthead.
 *
 * Fresh timestamps every call rather than a fixture, because `progressOn`
 * refuses anything older than two minutes. A CANCELLED run has no position at
 * all: a train that is not running is not anywhere.
 */
function samplePosition(station, row) {
  if (row.cancelled || !Number.isFinite(station?.lat) || !Number.isFinite(station?.lng)) return null;
  const now = Date.now();
  return {
    lat: station.lat,
    lng: station.lng,
    at: new Date(now - 20_000).toISOString(),
    expiresAt: new Date(now + 40_000).toISOString(),
    direction: null,
  };
}

/**
 * Three trains on a line, spread along it, with timestamps from right now.
 *
 * The COORDINATES ARE REAL — they are interpolated between two of the line's
 * own bundled stations, so a demo glyph sits on a piece of railway that exists
 * and `trainsOnLine` projects it exactly as it projects a live one. Only the
 * existence of the trains is invented, which is the same bargain `sampleRun`
 * makes: the shape is true, the feed is not.
 *
 * Fresh timestamps every call rather than a fixture, because `progressOn`
 * refuses anything older than two minutes — a frozen sample would draw nothing
 * at all, which is a demo of the empty state.
 *
 * Two inbound and two outbound, so `/demo` shows both glyph directions on one
 * screen. Inbound means towards the city, and the bundle stores every line city
 * end first, so the inbound one is signed for stop zero.
 *
 * The fourth sits ONE SEGMENT OUT FROM THE CITY, citybound. The line page
 * asks for the trains of every line its strip draws as a through continuation,
 * and this is the one that lands in the tunnel on that half — on the Cranbourne
 * page it is a Sunbury train between Parkville and State Library, drawn above
 * the city with the stops reversed, so it is the one that proves a citybound
 * train on the reversed half is drawn moving DOWN the screen.
 */
const SAMPLE_TRAINS = [
  { frac: 0.18, direction: 'Inbound', directionId: 1 },
  { frac: 0.5, direction: 'Outbound', directionId: 2 },
  { frac: 0.78, direction: 'Outbound', directionId: 2 },
  { frac: 0.07, direction: 'Inbound', directionId: 1 },
];

/** How far along its own segment a sample train sits. Not 0 or 1: those are stations. */
const SAMPLE_T = 0.45;

/**
 * The stops a demo board can be open at, the one it opens at FIRST.
 *
 * `/demo` seeds Carnegie as the default station (`app-state.js`), so 1034 is
 * the board somebody actually lands on and the only one worth matching first.
 * The rest are what the picker offers, and `1` is the chassis's own generic
 * "Station 1" seed, which has no pattern of its own and falls through to
 * Flinders Street's.
 */
const DEMO_BOARD_STOPS = [...new Set([1034, ...SAMPLE_STATIONS.map((s) => s.stopId), 1])];

/**
 * A run ref belonging to a demo BOARD ROW on this line, if there is one.
 *
 * The Trains tab pairs a row with a train by run ref, and the demo's two halves
 * had no ref in common — the boards mint `demo:<stop>:<row>` and the trains
 * minted `demo:train:<route>:<n>`, so `/demo` could never show a row where its
 * train was. Handing one sample train a real board row's ref joins them: the
 * demo's own bargain, which is that the shape is true and the feed is not.
 *
 * It is also the ref the line strip's glyph now opens, which `sampleRun` can
 * take apart — the invented one could not, and that tap led to an error page.
 *
 * A cancelled row is never chosen: a train that is not running is not somewhere.
 */
function demoBoardRef(routeId) {
  for (const stopId of DEMO_BOARD_STOPS) {
    const row = sampleDepartures(stopId)
      .find((d) => !d.cancelled && Number(d.routeId) === Number(routeId));
    if (row) return row.runRef;
  }
  return null;
}

export function sampleTrains(routeId) {
  const stops = lineStops(routeId);
  if (stops.length < 2) return [];

  const now = Date.now();
  // Twenty seconds old, expiring in forty: comfortably inside every refusal in
  // `progress.js`, and honest about what a real fix looks like.
  const at = new Date(now - 20_000).toISOString();
  const expiresAt = new Date(now + 40_000).toISOString();
  const last = stops.length - 1;

  return SAMPLE_TRAINS.map((spec, i) => {
    const index = Math.min(last - 1, Math.max(0, Math.round(spec.frac * (last - 1))));
    const from = stops[index];
    const to = stops[index + 1];
    return {
      // One of the three carries a demo board row's ref, so the Trains tab has
      // a row to write a phrase on. The other two are the line's own.
      runRef: (i === 0 ? demoBoardRef(routeId) : null) ?? `demo:train:${Number(routeId)}:${i}`,
      destination: (spec.direction === 'Inbound' ? stops[0]?.name : stops[last]?.name) ?? '',
      directionId: spec.directionId,
      status: 'scheduled',
      position: {
        lat: from.lat + (to.lat - from.lat) * SAMPLE_T,
        lng: from.lng + (to.lng - from.lng) * SAMPLE_T,
        at,
        expiresAt,
        direction: spec.direction,
      },
      vehicle: {
        description: '6 Car Xtrapolis', carriages: 6, lowFloor: null, airConditioned: null,
      },
    };
  });
}

/**
 * Which way along the corridor this sample train goes, and where it stops.
 *
 * By its DESTINATION first: a row signed Sunbury runs towards Sunbury and
 * terminates there, which is the only reading that survives the tunnel — at
 * State Library every train is outbound and the sign is the only thing that
 * says out of which end. Where the destination is not a station on the
 * corridor ("City (Town Hall)" is, once the parenthesis is opened; "City" on
 * its own is not) it falls back to the direction the board would show.
 */
// ── Sample station facts, and a sample fare ──────────────────────────────────

/**
 * Carnegie, as PTV really answers for it.
 *
 * Not invented: these are the fields a signed request actually returns for stop
 * 1034 — no lift, 108 car spaces, 5 bicycle racks, Zone 1. A demo station page
 * showing a lift at a station that has none would be teaching somebody
 * something false about a real building, which is the one thing `/demo` is not
 * allowed to do even while everything on it is a sample.
 */
const SAMPLE_STATION = {
  stopId: 1034,
  name: 'Carnegie',
  stationType: null,
  description: '',
  landmark: '',
  operatingHours: '',
  accessibility: {
    lift: false,
    escalator: false,
    stairs: true,
    hearingLoop: false,
    tactileGroundSurfaceIndicator: true,
    lighting: true,
    wheelchair: { accessibleRamp: true, parking: true, toilet: false, telephone: false },
  },
  amenities: {
    toilet: false,
    carParking: 108,
    bicycleRack: 5,
    bicycleCage: null,
    bicycleLocker: null,
    shelteredWaitingArea: true,
    indoorWaitingArea: false,
    cctv: true,
    payPhone: false,
    seatType: '',
    taxiRank: false,
  },
  ticket: {
    zone: 'Zone 1',
    ticketZones: [1],
    ticketMachine: true,
    ticketChecks: true,
    isFreeFareZone: false,
  },
};

/** "Zone 1", "Zone 1/2" — PTV's own spelling for a boundary station. */
const zoneWord = (zones) => (zones.length > 1 ? `Zone ${zones.join('/')}` : `Zone ${zones[0]}`);

/**
 * Carnegie's facts under whichever station the demo opened.
 *
 * The ZONE is taken from the bundle rather than from the sample, because the
 * bundle knows the real one for all 225 — so a demo station page at Werribee
 * says Zone 2, which is true, instead of Carnegie's Zone 1, which is not.
 */
export function sampleStation(stopId) {
  const id = GUESSED_IDS[Number(stopId)] ?? Number(stopId);
  const station = allStations().find((s) => s.stopId === id) ?? null;
  const zones = station?.zones?.length ? station.zones : SAMPLE_STATION.ticket.ticketZones;
  return {
    ...SAMPLE_STATION,
    stopId: id,
    name: station?.name ?? SAMPLE_STATION.name,
    ticket: { ...SAMPLE_STATION.ticket, zone: zoneWord(zones), ticketZones: [...zones] },
  };
}

/**
 * A Zone 1–2 fare, as PTV really quotes it: $2.85 two-hour, $5.70 daily.
 *
 * One fare for every pair the demo asks about. The alternative is a table of
 * invented prices, and a wrong price is the single worst thing this app could
 * put on a screen — somebody tops up a Myki against it.
 */
const SAMPLE_FARE = {
  full: { twoHour: 2.85, daily: 5.70 },
  concession: { twoHour: 1.42, daily: 2.85 },
};

function sampleRunPath(corridor, at, row) {
  const wanted = normaliseStationName(String(row.destination).replace(/^city\s*\(|\)$/gi, ''));
  const to = corridor.stops.findIndex((s) => normaliseStationName(s.name) === wanted);
  if (to >= 0 && to !== at) return { step: to > at ? 1 : -1, last: to };

  const direction = directionOf(row, corridor.stops[at]?.lines ?? [], { stopId: corridor.stops[at]?.stopId });
  const back = corridor.stops[at - 1] && directionBetween(corridor, at, at - 1) === direction;
  return { step: back ? -1 : 1, last: back ? 0 : corridor.stops.length - 1 };
}
